import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

interface Category {
  name: string;
  icon: string;
  url: string;
}

const categories: Category[] = [
  { name: 'Shoes', icon: 'ri-footprint-line', url: 'https://kaspreadsheet.com/category-911-Shoes.html' },
  { name: 'Hoodies & Sweaters', icon: 'ri-t-shirt-line', url: 'https://kaspreadsheet.com/search-912/name/Shoes.html' },
  { name: 'T-Shirts', icon: 'ri-t-shirt-line', url: 'https://kaspreadsheet.com/search-913/name/Shoes.html' },
  { name: 'Jackets', icon: 'ri-windy-line', url: 'https://kaspreadsheet.com/search-914/name/Shoes.html' },
  { name: 'Pants & Shorts', icon: 'ri-pantone-line', url: 'https://kaspreadsheet.com/search-915/name/Shoes.html' },
  { name: 'Headwear', icon: 'ri-gatsby-line', url: 'https://kaspreadsheet.com/search-916/name/Shoes.html' },
  { name: 'Sets', icon: 'ri-stack-line', url: 'https://kaspreadsheet.com/search-917/name/Shoes.html' },
  { name: 'Underwear', icon: 'ri-shirt-line', url: 'https://kaspreadsheet.com/search-918/name/Shoes.html' },
  { name: 'Jersey', icon: 'ri-vip-crown-line', url: 'https://kaspreadsheet.com/search-919/name/Shoes.html' },
  { name: 'Accessories', icon: 'ri-glasses-line', url: 'https://kaspreadsheet.com/search-920/name/Shoes.html' },
  { name: 'Bags', icon: 'ri-handbag-line', url: 'https://kaspreadsheet.com/search-921/name/Shoes.html' },
  { name: 'Belts', icon: 'ri-link', url: 'https://kaspreadsheet.com/search-922/name/Shoes.html' },
  { name: 'Perfumes', icon: 'ri-drop-line', url: 'https://kaspreadsheet.com/search-923/name/Shoes.html' },
  { name: 'Other Stuff', icon: 'ri-more-line', url: 'https://kaspreadsheet.com/search-924/name/Shoes.html' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleMouseEnter = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setDropdownOpen(true);
  };

  const handleMouseLeave = () => {
    closeTimerRef.current = setTimeout(() => {
      setDropdownOpen(false);
    }, 200);
  };

  const isTop = !scrolled;

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-gradient-to-b from-black/45 via-black/25 to-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center gap-6 md:gap-8">
            <Link
              to="/"
              className={`text-xl md:text-2xl font-heading font-bold whitespace-nowrap transition-colors duration-300 ${
                isTop ? 'text-white drop-shadow-sm' : 'text-foreground-950'
              }`}
            >
              VigorBuy
            </Link>

            <div className="hidden md:flex items-center gap-5">
              <Link
                to="/"
                className={`text-sm font-semibold whitespace-nowrap transition-colors duration-300 cursor-pointer ${
                  isTop ? 'text-white hover:text-white/80' : 'text-foreground-700 hover:text-foreground-950'
                }`}
              >
                Home
              </Link>
              <Link
                to="/articles/vigorbuy-spreadsheet-guide"
                className={`text-sm font-semibold whitespace-nowrap transition-colors duration-300 cursor-pointer ${
                  isTop ? 'text-white hover:text-white/80' : 'text-foreground-700 hover:text-foreground-950'
                }`}
              >
                Shopping Guide
              </Link>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className={`text-sm font-medium whitespace-nowrap transition-colors duration-300 cursor-pointer ${
                  isTop ? 'text-white/85 hover:text-white' : 'text-foreground-600 hover:text-foreground-950'
                }`}
              >
                Spreadsheet
              </a>

              <div
                ref={dropdownRef}
                className="relative"
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              >
                <button
                  className={`flex items-center gap-1 text-sm font-medium whitespace-nowrap transition-colors duration-300 cursor-pointer ${
                    isTop ? 'text-white/85 hover:text-white' : 'text-foreground-600 hover:text-foreground-950'
                  }`}
                >
                  Categories
                  <i className={`ri-arrow-down-s-line transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
                </button>

                {dropdownOpen && (
                  <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-60 bg-background-50 rounded-xl border border-background-200/70 py-3 overflow-hidden shadow-lg">
                    {categories.map((cat) => (
                      <a
                        key={cat.name}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:text-foreground-950 hover:bg-background-100 transition-colors duration-150 whitespace-nowrap cursor-pointer"
                      >
                        <i className={`${cat.icon} text-base text-foreground-400 w-5 h-5 flex items-center justify-center`}></i>
                        {cat.name}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className={`hidden md:block px-5 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-all duration-300 cursor-pointer ${
                isTop
                  ? 'bg-white text-foreground-950 hover:bg-white/90'
                  : 'bg-primary-500 text-white hover:bg-primary-600'
              }`}
            >
              Vigorbuy Sheet
            </a>

            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className={`md:hidden w-9 h-9 flex items-center justify-center rounded-lg cursor-pointer transition-colors duration-300 ${
                isTop ? 'text-white' : 'text-foreground-950'
              }`}
              aria-label="Toggle menu"
            >
              <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 max-h-[80vh] overflow-y-auto">
          <div className="px-4 py-3 flex flex-col gap-1">
            <Link
              to="/"
              className="flex items-center gap-3 py-3 text-sm font-semibold text-foreground-950 cursor-pointer"
              onClick={() => setMobileOpen(false)}
            >
              <i className="ri-home-4-line text-lg w-5 h-5 flex items-center justify-center"></i>
              Home
            </Link>
            <Link
              to="/articles/vigorbuy-spreadsheet-guide"
              className="flex items-center gap-3 py-3 text-sm font-semibold text-primary-600 cursor-pointer"
              onClick={() => setMobileOpen(false)}
            >
              <i className="ri-book-open-line text-lg w-5 h-5 flex items-center justify-center"></i>
              Shopping Guide
            </Link>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center gap-3 py-3 text-sm font-medium text-foreground-700 cursor-pointer"
              onClick={() => setMobileOpen(false)}
            >
              <i className="ri-external-link-line text-lg w-5 h-5 flex items-center justify-center"></i>
              Spreadsheet
            </a>

            <button
              onClick={() => setMobileCatOpen(!mobileCatOpen)}
              className="flex items-center justify-between py-3 text-sm font-semibold text-foreground-950 cursor-pointer"
            >
              <span className="flex items-center gap-3">
                <i className="ri-grid-line text-lg w-5 h-5 flex items-center justify-center"></i>
                Categories
              </span>
              <i className={`ri-arrow-down-s-line text-lg transition-transform duration-200 ${mobileCatOpen ? 'rotate-180' : ''}`}></i>
            </button>

            {mobileCatOpen && (
              <div className="pl-4 border-l-2 border-background-200 ml-2 mb-2">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-3 py-2.5 text-sm text-foreground-600 hover:text-foreground-950 cursor-pointer"
                    onClick={() => setMobileOpen(false)}
                  >
                    <i className={`${cat.icon} text-base w-5 h-5 flex items-center justify-center text-foreground-400`}></i>
                    {cat.name}
                  </a>
                ))}
              </div>
            )}

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="mt-3 px-5 py-3 rounded-full bg-primary-500 text-white text-sm font-semibold text-center whitespace-nowrap cursor-pointer"
              onClick={() => setMobileOpen(false)}
            >
              Vigorbuy Sheet
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}